import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@werkstatt/ui';
import { Button } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { Label } from '@werkstatt/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@werkstatt/ui';
import { Textarea } from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import type { Booking, BookingCreate, BookingUpdate } from '../../types/booking';
import type { Vehicle } from '../../types/vehicle';
import type { Customer } from '../../types/customer';
import { calculateBookingDays } from '../../types/booking';

interface BookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: Booking | null;
  onSuccess: () => void;
}

export function BookingDialog({
  open,
  onOpenChange,
  booking,
  onSuccess,
}: BookingDialogProps) {
  const { toast } = useToast();
  const isEdit = !!booking;

  const [formData, setFormData] = useState<Partial<BookingCreate>>({
    booking_number: '',
    customer_id: 0,
    vehicle_id: 0,
    start_date: '',
    end_date: '',
    base_price_cents: 0,
    extras_price_cents: 0,
    insurance_price_cents: 0,
    total_price_cents: 0,
    deposit_cents: 50000,
    deposit_paid: false,
    status: 'pending',
    payment_status: 'unpaid',
    paid_amount_cents: 0,
    is_online_booking: false,
  });

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers?page_size=100');
      return response.data;
    },
  });

  // Fetch vehicles
  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      const response = await api.get('/vehicles?page_size=100');
      return response.data;
    },
  });

  // Reset form when dialog opens/closes or booking changes
  useEffect(() => {
    if (booking) {
      setFormData({
        booking_number: booking.booking_number,
        customer_id: booking.customer_id,
        vehicle_id: booking.vehicle_id,
        start_date: booking.start_date,
        start_time: booking.start_time || undefined,
        end_date: booking.end_date,
        end_time: booking.end_time || undefined,
        pickup_location: booking.pickup_location || '',
        return_location: booking.return_location || '',
        base_price_cents: booking.base_price_cents,
        extras_price_cents: booking.extras_price_cents,
        insurance_price_cents: booking.insurance_price_cents,
        total_price_cents: booking.total_price_cents,
        planned_km: booking.planned_km || undefined,
        deposit_cents: booking.deposit_cents || 50000,
        deposit_paid: booking.deposit_paid,
        deposit_payment_method: booking.deposit_payment_method || '',
        status: booking.status,
        payment_status: booking.payment_status,
        paid_amount_cents: booking.paid_amount_cents,
        customer_notes: booking.customer_notes || '',
        internal_notes: booking.internal_notes || '',
        is_online_booking: booking.is_online_booking,
      });
    } else {
      // Generate booking number
      const now = new Date();
      const year = now.getFullYear();
      const bookingNum = `BK-${year}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;

      setFormData({
        booking_number: bookingNum,
        customer_id: 0,
        vehicle_id: 0,
        start_date: '',
        end_date: '',
        base_price_cents: 0,
        extras_price_cents: 0,
        insurance_price_cents: 0,
        total_price_cents: 0,
        deposit_cents: 50000,
        deposit_paid: false,
        status: 'pending',
        payment_status: 'unpaid',
        paid_amount_cents: 0,
        is_online_booking: false,
      });
    }
  }, [booking, open]);

  // Calculate price when dates or vehicle changes
  useEffect(() => {
    if (formData.vehicle_id && formData.start_date && formData.end_date && vehiclesData) {
      const vehicle = vehiclesData.vehicles.find((v: Vehicle) => v.id === formData.vehicle_id);
      if (vehicle) {
        const days = calculateBookingDays(formData.start_date, formData.end_date);
        const basePrice = vehicle.price_per_day_cents * days;
        const total = basePrice + (formData.extras_price_cents || 0) + (formData.insurance_price_cents || 0);

        setFormData((prev) => ({
          ...prev,
          base_price_cents: basePrice,
          total_price_cents: total,
          deposit_cents: vehicle.deposit_cents,
        }));
      }
    }
  }, [formData.vehicle_id, formData.start_date, formData.end_date, formData.extras_price_cents, formData.insurance_price_cents, vehiclesData]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: BookingCreate) => {
      const response = await api.post('/bookings', data);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: 'Buchung erstellt',
        description: 'Die Buchung wurde erfolgreich erstellt.',
      });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description:
          (isAxiosError(error) ? error.response?.data?.detail : undefined) ||
          'Die Buchung konnte nicht erstellt werden.',
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: BookingUpdate) => {
      const response = await api.put(`/bookings/${booking!.id}`, data);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: 'Buchung aktualisiert',
        description: 'Die Buchung wurde erfolgreich aktualisiert.',
      });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description:
          (isAxiosError(error) ? error.response?.data?.detail : undefined) ||
          'Die Buchung konnte nicht aktualisiert werden.',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    if (!formData.customer_id || formData.customer_id === 0) {
      toast({
        title: 'Fehler',
        description: 'Bitte wählen Sie einen Kunden aus.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.vehicle_id || formData.vehicle_id === 0) {
      toast({
        title: 'Fehler',
        description: 'Bitte wählen Sie ein Fahrzeug aus.',
        variant: 'destructive',
      });
      return;
    }

    if (isEdit) {
      updateMutation.mutate(formData as BookingUpdate);
    } else {
      createMutation.mutate(formData as BookingCreate);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const customers: Customer[] = customersData?.customers || [];
  const vehicles: Vehicle[] = vehiclesData?.vehicles || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEdit ? 'Buchung bearbeiten' : 'Neue Buchung'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Booking Details */}
          <div className="space-y-4">
            <h3 className="font-semibold">Buchungsdetails</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="booking_number">Buchungsnummer *</Label>
                <Input
                  id="booking_number"
                  required
                  value={formData.booking_number}
                  onChange={(e) =>
                    setFormData({ ...formData, booking_number: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, status: value as BookingCreate['status'] })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Ausstehend</SelectItem>
                    <SelectItem value="confirmed">Bestätigt</SelectItem>
                    <SelectItem value="picked_up">Abgeholt</SelectItem>
                    <SelectItem value="returned">Zurückgegeben</SelectItem>
                    <SelectItem value="completed">Abgeschlossen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="customer_id">Kunde *</Label>
                <Select
                  value={formData.customer_id?.toString()}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, customer_id: parseInt(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Kunde auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id.toString()}>
                        {customer.first_name} {customer.last_name}
                        {customer.customer_number && ` (${customer.customer_number})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="vehicle_id">Fahrzeug *</Label>
                <Select
                  value={formData.vehicle_id?.toString()}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, vehicle_id: parseInt(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Fahrzeug auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id.toString()}>
                        {vehicle.manufacturer} {vehicle.model} ({vehicle.internal_number})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Date Range */}
          <div className="space-y-4">
            <h3 className="font-semibold">Zeitraum</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Startdatum *</Label>
                <Input
                  id="start_date"
                  type="date"
                  required
                  value={formData.start_date}
                  onChange={(e) =>
                    setFormData({ ...formData, start_date: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="end_date">Enddatum *</Label>
                <Input
                  id="end_date"
                  type="date"
                  required
                  value={formData.end_date}
                  onChange={(e) =>
                    setFormData({ ...formData, end_date: e.target.value })
                  }
                />
              </div>
            </div>
            {formData.start_date && formData.end_date && (
              <div className="text-sm text-muted-foreground">
                Dauer: {calculateBookingDays(formData.start_date, formData.end_date)} Tage
              </div>
            )}
          </div>

          {/* Prices */}
          <div className="space-y-4">
            <h3 className="font-semibold">Preise</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="base_price_cents">Grundpreis (€)</Label>
                <Input
                  id="base_price_cents"
                  type="number"
                  min="0"
                  step="0.01"
                  value={((formData.base_price_cents || 0) / 100).toFixed(2)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      base_price_cents: Math.round(parseFloat(e.target.value) * 100) || 0,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="extras_price_cents">Extras (€)</Label>
                <Input
                  id="extras_price_cents"
                  type="number"
                  min="0"
                  step="0.01"
                  value={((formData.extras_price_cents || 0) / 100).toFixed(2)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      extras_price_cents: Math.round(parseFloat(e.target.value) * 100) || 0,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="insurance_price_cents">Versicherung (€)</Label>
                <Input
                  id="insurance_price_cents"
                  type="number"
                  min="0"
                  step="0.01"
                  value={((formData.insurance_price_cents || 0) / 100).toFixed(2)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      insurance_price_cents: Math.round(parseFloat(e.target.value) * 100) || 0,
                    })
                  }
                />
              </div>
            </div>
            <div>
              <Label htmlFor="total_price_cents">Gesamtpreis (€) *</Label>
              <Input
                id="total_price_cents"
                type="number"
                min="0"
                step="0.01"
                required
                value={((formData.total_price_cents || 0) / 100).toFixed(2)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    total_price_cents: Math.round(parseFloat(e.target.value) * 100) || 0,
                  })
                }
              />
            </div>
          </div>

          {/* Deposit */}
          <div className="space-y-4">
            <h3 className="font-semibold">Kaution</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="deposit_cents">Kaution (€)</Label>
                <Input
                  id="deposit_cents"
                  type="number"
                  min="0"
                  step="0.01"
                  value={((formData.deposit_cents || 0) / 100).toFixed(2)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      deposit_cents: Math.round(parseFloat(e.target.value) * 100) || 0,
                    })
                  }
                />
              </div>
              <div className="flex items-center space-x-2 pt-8">
                <input
                  type="checkbox"
                  id="deposit_paid"
                  checked={formData.deposit_paid}
                  onChange={(e) =>
                    setFormData({ ...formData, deposit_paid: e.target.checked })
                  }
                  className="h-4 w-4"
                />
                <Label htmlFor="deposit_paid">Kaution bezahlt</Label>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <h3 className="font-semibold">Notizen</h3>
            <div>
              <Label htmlFor="customer_notes">Kundennotizen</Label>
              <Textarea
                id="customer_notes"
                value={formData.customer_notes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormData({ ...formData, customer_notes: e.target.value })
                }
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="internal_notes">Interne Notizen</Label>
              <Textarea
                id="internal_notes"
                value={formData.internal_notes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormData({ ...formData, internal_notes: e.target.value })
                }
                rows={2}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Wird gespeichert...' : isEdit ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
